import useFetchAndTrace from "./fetchWithTrace";
import { useCustomAlert } from "./alerts";
import { useCallback, useState } from "react";

export function useDownloadFileFromWebApp() {
  const fetchAndTrace = useFetchAndTrace();
  const customAlert = useCustomAlert();
  const [downloading, setDownloading] = useState(false);

  const errorHandler = useCallback(() => {
    setDownloading(false);
    customAlert("Failed to download file");
  }, [customAlert]);

  const download = useCallback(
    (url: string, filename?: string) => {
      const getPickAndPlaceHandler = async (response: Response) => {
        setDownloading(false);
        if (response.ok) {
          const blob = await response.blob();
          const link = document.createElement("a");
          link.download = filename || "";
          link.href = URL.createObjectURL(blob);
          link.click();
        } else {
          throw Error("Failed to get pick and place");
        }
      };

      setDownloading(true);
      void fetchAndTrace(url, getPickAndPlaceHandler, errorHandler);
    },
    [errorHandler, fetchAndTrace],
  );

  return {
    download,
    downloading,
  };
}

export function stringToBlob(content: string) {
  return new Blob([content], { type: "text/plain" });
}

export function downloadBlob(
  blob: Blob,
  filename: string,
  openLink?: boolean,
): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Apparently the append and remove child are for older browser...
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (openLink) {
    window.open(url);
  }
  // Clean up the URL object to prevent memory leaks
  URL.revokeObjectURL(url);
}

export function readFileAsString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target !== null) {
        resolve(e.target.result as string);
      } else {
        reject(Error("Could not read file"));
      }
    };
    reader.onerror = () => {
      reject(Error("Could not read file"));
    };
    reader.readAsText(file);
  });
}

export function changeFileExtension(
  filename: string,
  newExtension: string,
): string {
  // Make sure the extension has no dot
  const extensionWithoutDot = newExtension.replaceAll(".", "");

  const parts = filename.split(".");

  // If no extension
  if (parts.length === 1) {
    return `${filename}.${extensionWithoutDot}`;
  }

  // If hidden file like ".gitignore"
  if (parts.length === 2 && parts[0] === "") {
    return `${filename}.${extensionWithoutDot}`;
  }

  // Replace the last part with the new extension
  parts[parts.length - 1] = extensionWithoutDot;
  return parts.join(".");
}
